<template>
  <div v-if="isReportTab" class="jmreport-report-page">
    <iframe
      class="jmreport-report-page__iframe"
      :src="reportPath"
      frameborder="0"
    />
  </div>
  <cargo-left-right-resize
    v-else
    :left-style="{ flex: '1 1 auto', width: '100%', 'max-width': 'none' }"
    :right-style="{ display: 'none' }"
    style="min-height: 100%;"
    @onSize="resizeCache = $event"
  >
    <template slot="left">
      <div v-loading="loading" class="jmreport-left">
        <div class="jmreport-left__filter">
          <el-input
            v-model="filterText"
            class="jmreport-left__search"
            size="small"
            clearable
            placeholder="搜索报表"
          />
          <el-button size="mini" type="primary" @click="toDesignReport">设计报表</el-button>
        </div>
        <cargo-ag-grid
          :show-side-bar="false"
          :grid-options="jmReportTree.gridOptions"
          :column-defs="jmReportTree.columnDefs"
          :row-data="jmReportTree.rowData"
          style="width: 100%; height: 100%;"
          @gridReady="params=>{gridApi=params.api}"
        />
      </div>
    </template>

    <template slot="right" />
  </cargo-left-right-resize>
</template>

<script>
import cargoLeftRightResize from '@/components/Cargo/Common/cargoLeftRightResize.vue'
import LocalStorageUtilsNew from '@/utils/localStorageUtilsNew'
import checkPermission from '@/utils/permission'
import { getToken, getUserInfo } from '@/utils/auth'
import { getJmReportMenuTree } from '@/api/jmReport'
import { filterMenuTreeByPermission } from './jmReportTree'

function safeLabel(node) {
  return node.label || node.chineseLabel || node.englishLabel || node.chineseName || node.name || node.reportCode || node.code || node.uuid || ''
}

function flattenTree(nodes, parentLabels = []) {
  const list = Array.isArray(nodes) ? nodes : []
  const rows = []
  list.forEach(n => {
    const label = safeLabel(n)
    const pathLabels = parentLabels.concat([label])
    rows.push({
      uuid: n.uuid,
      reportId: n.reportId,
      reportCode: n.reportCode,
      chineseName: n.chineseName,
      englishName: n.englishName,
      label,
      // Used by ag-grid treeData getDataPath()
      path: pathLabels.join(','),
      // Actual report URL.
      reportPath: n.path || n.url || n.h5Path || ''
    })
    const children = Array.isArray(n.childNods) ? n.childNods : (Array.isArray(n.children) ? n.children : [])
    if (children.length > 0) {
      rows.push(...flattenTree(children, pathLabels))
    }
  })
  return rows
}

const JM_REPORT_MENU_ID = 5100

export default {
  name: 'JmReport',
  components: { cargoLeftRightResize },
  data() {
    return {
      loading: false,
      filterText: '',
      gridApi: null,
      jmReportTree: {
        columnDefs: [
          {
            headerName: 'Path',
            field: 'path',
            width: 80,
            hide: true
          }
        ],
        rowData: [],
        gridOptions: {
          rowSelection: 'single',
          groupDefaultExpanded: -1,
          suppressRowClickSelection: false,
          treeData: true,
          getDataPath: (data) => {
            return String(data.path || '').split(',').filter(Boolean)
          },
          autoGroupColumnDef: {
            headerName: '报表',
            width: 280,
            cellRendererParams: {
              suppressCount: true,
              suppressDoubleClickExpand: false,
              innerRenderer: (params) => {
                if (!params || !params.data) return ''
                return params.data.label || ''
              }
            },
            suppressMenu: true
          },
          onRowClicked: this.onRowClicked
        }
      }
    }
  },
  computed: {
    resizeCache: {
      get() {
        return LocalStorageUtilsNew.getConfiguration('jmReportResizeCache') || {}
      },
      set(val) {
        LocalStorageUtilsNew.setConfiguration('jmReportResizeCache', val || {})
      }
    },
    isReportTab() {
      return !!(this.$route && this.$route.query && this.$route.query.reportPath)
    },
    reportPath() {
      return this.appendToken((this.$route && this.$route.query && this.$route.query.reportPath) || '')
    }
  },
  watch: {
    filterText(val) {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(val || '')
      }
    }
  },
  mounted() {
    if (!this.isReportTab) {
      this.fetchTree()
    }
  },
  methods: {
    toDesignReport() {
      const cargo = process.env.VUE_APP_CARGO || ''
      const url = this.appendToken(`${cargo}/jmreport/list`)
      window.open(url, '_blank')
    },
    async fetchTree() {
      this.loading = true
      try {
        const res = await getJmReportMenuTree(JM_REPORT_MENU_ID)
        const data = res && res.data
        const list = Array.isArray(data) ? data : ((data && Array.isArray(data.data)) ? data.data : [])
        this.jmReportTree.rowData = flattenTree(filterMenuTreeByPermission(list, this.hasMenuPermission))
      } catch (e) {
        // Keep UI usable even if API fails.
        const msg = (e && e.message) ? e.message : '加载失败'
        this.$message && this.$message.error(msg)
      } finally {
        this.loading = false
      }
    },
    hasMenuPermission(code) {
      const roles = (this.$store && this.$store.getters && this.$store.getters.roles) || []
      return !!code && (roles.includes(code) || checkPermission([code]))
    },
    onRowClicked(params) {
      const data = params && params.data ? params.data : null
      if (!data) return
      if (data.reportPath) {
        this.openReportTab(data)
      }
    },
    getSessionId() {
      const getters = (this.$store && this.$store.getters) || {}
      const userInfo = getters.userInfo || getUserInfo() || {}
      return getters.token || userInfo.sessionId || getToken()
    },
    appendToken(path) {
      const token = this.getSessionId()
      if (!path || !token) return path
      if (/[?&]token=/.test(path)) return path
      const connector = path.includes('?') ? '&' : '?'
      return `${path}${connector}token=${encodeURIComponent(token)}`
    },
    openReportTab(data) {
      const key = data.uuid || data.reportId || data.reportCode || data.reportPath
      this.$router.push({
        name: this.$route.name,
        query: {
          openNew: true,
          key,
          title: data.label || data.chineseName || data.reportCode || '报表',
          reportPath: this.appendToken(data.reportPath)
        }
      })
    }
  }
}
</script>

<style scoped>
.jmreport-left {
  width: 100%;
  height: 100%;
  min-width: 190px;
  padding: 8px;
  background-color: #F5F7FA;
  box-sizing: border-box;
  overflow: hidden;
}

.jmreport-report-page {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

.jmreport-report-page__iframe {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.jmreport-left__filter {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
}

.jmreport-left__search {
  flex: 1;
}

.jmreport-right {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.jmreport-right__header {
  padding: 10px 12px;
  border-bottom: 1px solid #EBEEF5;
  background: #fff;
  box-sizing: border-box;
}

.jmreport-right__header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.jmreport-right__title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.jmreport-right__path {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  word-break: break-all;
}

.jmreport-right__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  background: #fff;
}

.jmreport-right__iframe {
  flex: 1;
  width: 100%;
  border: 0;
  background: #fff;
}
</style>
